import { formatMessage } from "lib/globalize-util";
import { COLLECTIONS } from "constants/action-types";
import { EventTypes } from "redux-segment";
import { getValueFromLocalStorage } from "helpers/product-collection";
import { IDoor } from "helpers/door-collection";
import { IExportParams, NAMESPACE_KEYS } from "constants/product-collection";
import { GridApi, ColumnApi } from "ag-grid-community";
import { RollupType } from "containers/folders/types/server-rollups";
import { List, Map } from "immutable";
import { IImageCellDimensions } from "constants/types";
import { ISnapshotItem } from "helpers/server-rollup";
import { SaveAssormentSettingsRequest } from "components/product-collection/market-settings/market-settings.types";
import { IDoorTotals } from "reducers/collection";
import { IAllocationDimension } from "helpers/allocation-dimension";

interface IUploadData {
    content_type: string;
    filename: string;
    filesize_byte: number | string;
}

interface IChatMessage {
    kind?: string;
    content: string;
}

interface IChatMeta {
    identity?: string;
    namespace?: string;
}

export interface ICopyProps {
    brandId: string;
    collectionId: string;
    collectionType: string;
    name: string;
    withItems: boolean;
    withPermissions: boolean;
    withDefaultRow: boolean;
    withDoorLabels?: boolean;
    allocationData?: boolean;
    withPODetails?: boolean;
    withTargetData?: boolean;
    planData?: boolean;
    excludedAllocationDimensionValues?: { [allocationDimensionId: string]: string[] };
    allocationDimensionValues?: { [allocationDimensionId: string]: string[] };
    parentId: string;
    merchantId?: string;
    targetMerchantId?: string;
    bagVersion?: number;
    collectionPoiId?: string;
    callback?: (assortmentId: string, error: any) => void;
    collectionAllocationDimensions?: IAllocationDimension[];
}

export interface ICreateCollectionParams {
    parentId?: string;
    brandId?: string;
    merchantId?: string;
    name?: string;
    excludedAllocationDimensionValues?: {
        [allocationDimensionId: string]: string[];
    };
}

export const createCollection = (params: ICreateCollectionParams = {}) => ({
    meta: {
        analytics: EventTypes.track,
    },
    payload: {
        brandId: params.brandId,
        name: params.name || formatMessage("Untitled Assortment"),
        merchantId: params.merchantId,
        parentId: params.parentId,
        excludedAllocationDimensionValues: params.excludedAllocationDimensionValues,
    },
    type: COLLECTIONS.COLLECTION.CREATE.START,
});

export interface ICopyCollectionAction {
    meta: {
        analytics: string;
    };
    payload: ICopyProps;
    type: string;
}

export const copyCollection = (params: ICopyProps): ICopyCollectionAction => ({
    meta: {
        analytics: EventTypes.track,
    },
    payload: {
        ...params,
        name: params.name || formatMessage("Untitled Assortment"),
    },
    type: COLLECTIONS.COLLECTION.COPY.START,
});

export const refreshPage = () => ({
    type: COLLECTIONS.COLLECTION.REFRESH_PAGE,
});

export const copyCollectionSuccess = (payload) => ({
    payload,
    type: COLLECTIONS.COLLECTION.COPY.COMPLETED,
});

export const createCollectionSuccess = (payload) => ({
    payload,
    type: COLLECTIONS.COLLECTION.CREATE.COMPLETED,
});

export const createCollectionError = (payload, error) => ({
    meta: {
        analytics: EventTypes.track,
    },
    error,
    payload,
    type: COLLECTIONS.COLLECTION.CREATE.COMPLETED,
});

export const copyCollectionError = (payload, error) => ({
    meta: {
        analytics: EventTypes.track,
    },
    error,
    payload,
    type: COLLECTIONS.COLLECTION.COPY.COMPLETED,
});

export const clearCollection = (itemsId?) => ({
    payload: {
        itemsId,
    },
    type: COLLECTIONS.COLLECTION.CLEAR,
});

export const deleteCollection = (id) => ({
    meta: {
        analytics: EventTypes.track,
    },
    payload: { id },
    type: COLLECTIONS.COLLECTION.DELETE.START,
});

export const deleteCollectionSuccess = (payload) => ({
    payload,
    type: COLLECTIONS.COLLECTION.DELETE.COMPLETED,
});

export const deleteCollectionError = (payload, error) => ({
    error,
    payload,
    type: COLLECTIONS.COLLECTION.DELETE.COMPLETED,
});

export const syncCollectionObject = (objectNamespace, objectId) => ({
    payload: {
        objectNamespace,
        objectId,
    },
    type: COLLECTIONS.COLLECTION.OBJECT.SYNC.START,
});

export const syncRetryCollectionObject = (objectNamespace, objectId) => ({
    payload: {
        objectNamespace,
        objectId,
    },
    type: COLLECTIONS.COLLECTION.OBJECT.SYNC.RETRY,
});

export const fetchCollectionById = (
    ids: string[],
    userId?: string,
    isRollupMode?: boolean,
    rollupType?: RollupType,
    isServerRollup?: boolean,
    isCloseoutBrandEnabled?: boolean
) => ({
    payload: {
        ids,
        userId,
        isRollupMode,
        rollupType,
        isServerRollup,
        isCloseoutBrandEnabled,
    },
    type: COLLECTIONS.COLLECTION.FETCH.START,
});

export const createCollectionAsset = (
    collectionId,
    uploadData: IUploadData,
    fileData,
    item,
    index,
    itemsId,
    key,
    assetIndex
) => ({
    payload: {
        collectionId,
        uploadData,
        fileData,
        item,
        index,
        itemsId,
        key,
        assetIndex,
    },
    type: COLLECTIONS.COLLECTION_MEDIA.CREATE.START,
});

export const deleteCollectionAsset = (
    collectionId: string,
    item: any,
    index: number,
    itemsId: string,
    key: string
) => ({
    payload: {
        collectionId,
        item,
        index,
        itemsId,
        key,
    },
    type: COLLECTIONS.COLLECTION_MEDIA.DELETE.START,
});

export const createCollectionAssetSuccess = (payload) => ({
    payload,
    type: COLLECTIONS.COLLECTION_MEDIA.CREATE.COMPLETED,
});

export const createCollectionAssetError = (payload, error) => ({
    payload,
    error,
    type: COLLECTIONS.COLLECTION_MEDIA.CREATE.COMPLETED,
});

export const addHashtags = (hashtags: string[]) => ({
    payload: { hashtags },
    type: COLLECTIONS.COLLECTION.HASHTAGS.ADD,
});

export const registerGridApi = (gridApi: GridApi, columnApi: ColumnApi) => ({
    payload: { gridApi, columnApi },
    type: COLLECTIONS.COLLECTION.GRID.REGISTER,
});

export const unregisterGridApi = () => ({
    type: COLLECTIONS.COLLECTION.GRID.UNREGISTER,
});

export interface IGridXlsExportAction {
    type: string;
    payload: {
        collection: any;
        assortmentIds?: string[];
    };
}
export const gridXlsExport = (collection: any, assortmentIds?: string[]): IGridXlsExportAction => ({
    type: COLLECTIONS.COLLECTION.GRID.XLS_EXPORT,
    payload: {
        collection,
        assortmentIds,
    },
});

export const updateCollectionAttribute = (id: string, prop: string, value: any, reset = false) => ({
    meta: {
        analytics: {
            eventType: EventTypes.track,
            eventPayload: {
                reset,
            },
        },
    },
    payload: {
        id,
        prop,
        value,
    },
    type: COLLECTIONS.COLLECTION.UPDATE.START,
});

export const openOrCloseCollectionSettings = (payload: boolean = true) => ({
    payload,
    type: COLLECTIONS.COLLECTION.UPDATE_COLLECTION_SETTINGS.OPEN_CLOSE,
});

export const updateCollectionSettings = (payload: SaveAssormentSettingsRequest) => ({
    payload,
    type: COLLECTIONS.COLLECTION.UPDATE_COLLECTION_SETTINGS.START,
});

export const getOrCreateSession = (collectionId: string, userId: string, merchantId: string, editable = true) => {
    const sessionId = getValueFromLocalStorage(
        collectionId,
        userId,
        merchantId,
        editable ? NAMESPACE_KEYS.SESSION_READ_WRITE : NAMESPACE_KEYS.SESSION_READ_ONLY
    );
    if (sessionId) {
        return createSessionSuccess(collectionId, sessionId);
    }

    return createSession(collectionId, merchantId, { editable });
};

export const createSession = (collectionId: string, merchantId: string, meta: any = {}) => ({
    payload: {
        collectionId,
        merchantId,
    },
    meta,
    type: COLLECTIONS.COLLECTION.SESSION.CREATE.START,
});

export const createSessionSuccess = (collectionId: string, sessionId) => ({
    payload: {
        collectionId,
        sessionId,
    },
    type: COLLECTIONS.COLLECTION.SESSION.CREATE.COMPLETED,
});

export const clearSession = () => ({
    type: COLLECTIONS.COLLECTION.SESSION.CLEAR,
});

export const subscribeToItemsUpdates = (collectionId: string, itemsId: string, sessionId: string) => ({
    payload: {
        collectionId,
        itemsId,
        sessionId,
    },
    type: COLLECTIONS.COLLECTION.ITEMS.UPDATES.SUBSCRIBE,
});

export const unsubscribeToItemsUpdates = (collectionId: string, itemsId: string) => ({
    payload: {
        collectionId,
        itemsId,
    },
    type: COLLECTIONS.COLLECTION.ITEMS.UPDATES.UNSUBSCRIBE,
});

export const updateReceived = (collectionId: string, objectId: string, data: any[]) => ({
    payload: {
        collectionId,
        objectId,
        data,
    },
    type: COLLECTIONS.COLLECTION.UPDATES.RECEIVED,
});

export const checkForProductUpdates = (collectionId) => ({
    payload: {
        collectionId,
    },
    type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.START,
});

export const clearProductUpdates = () => ({
    type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CLEAR,
});

export const cancelCheckForProductUpdates = () => ({
    type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.UPDATES.CHECK.STOP,
});

export const syncProductsStart = ({ collectionId, isFiltered }: { collectionId: string; isFiltered?: boolean }) => ({
    payload: {
        collectionId,
        isFiltered,
    },
    type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.START,
});

export const syncProductsStop = () => ({
    type: COLLECTIONS.COLLECTION.ITEMS.PRODUCTS.SYNC.STOP,
});

export const itemsSyncConflict = (payload, error) => ({
    type: COLLECTIONS.COLLECTION.ITEMS.SYNC.CONFLICT,
    meta: {
        collectionId: payload.collectionId,
        objectId: payload.objectId,
    },
    payload: error,
    error: true,
});

export const setSessionCellFocus = (collectionId: string, itemsId: string, field) => ({
    payload: {
        collectionId,
        itemsId,
        field,
    },
    type: COLLECTIONS.COLLECTION.SESSION.FOCUS.SET,
});

export const subscribeToSessionUpdates = (collectionId) => ({
    payload: {
        collectionId,
    },
    type: COLLECTIONS.COLLECTION.SESSION.UPDATES.SUBSCRIBE,
});

export const unsubscribeToSessionUpdates = () => ({
    type: COLLECTIONS.COLLECTION.SESSION.UPDATES.UNSUBSCRIBE,
});

export const sessionUpdateReceived = (collectionId, activeUserSessions, focusOnCellMap) => ({
    payload: {
        collectionId,
        activeUserSessions,
        focusOnCellMap,
    },
    type: COLLECTIONS.COLLECTION.SESSION.UPDATES.RECEIVED,
});

export const createCollectionExport = (payload: IExportParams) => {
    return {
        payload,
        type: COLLECTIONS.COLLECTION.EXPORT.CREATE.START,
    };
};

export const createNordstromMessage = (payload) => ({
    payload,
    type: COLLECTIONS.COLLECTION.NORDSTROM.CREATE.START,
});

export const createCollectionExportSuccess = (collectionExport) => ({
    payload: { collectionExport },
    type: COLLECTIONS.COLLECTION.EXPORT.CREATE.COMPLETED,
});

export const createCollectionExportError = (collectionExport) => ({
    payload: { collectionExport },
    error: true,
    type: COLLECTIONS.COLLECTION.EXPORT.CREATE.COMPLETED,
});

export const pollCollectionExport = (collectionId: String, exportId: String, rollupSnapshotId?: String) => ({
    payload: { collectionId, exportId, rollupSnapshotId },
    type: COLLECTIONS.COLLECTION.EXPORT.POLL,
});

export const receivedCollectionExport = (collectionExport) => ({
    payload: { collectionExport },
    type: COLLECTIONS.COLLECTION.EXPORT.RECEIVED,
});

export const clearCollectionExport = () => ({ type: COLLECTIONS.COLLECTION.EXPORT.CLEAR });

export const getCollectionChatMessages = (collectionId: string, options = {}) => {
    if (!(options as any).limit) {
        (options as any).limit = 30;
    }

    return {
        payload: { collectionId, options },
        type: COLLECTIONS.COLLECTION_CHAT.FETCH_MESSAGES.START,
    };
};

export const unsubscribeToChatUpdates = ({ namespace, id }) => ({
    payload: {
        namespace,
        id,
    },
    type: COLLECTIONS.COLLECTION_CHAT.UPDATES.UNSUBSCRIBE,
});

export const createCollectionChatMessage = (collectionId: string, data: IChatMessage, meta?: IChatMeta) => ({
    payload: {
        collectionId,
        data,
    },
    meta,
    type: COLLECTIONS.COLLECTION_CHAT.CREATE_MESSAGE.START,
});

export const deleteCollectionChatMessage = (messageId: string, chatThreadId: string, collectionId: string) => ({
    payload: {
        messageId,
        chatThreadId,
        collectionId,
    },
    type: COLLECTIONS.COLLECTION_CHAT.DELETE_MESSAGE.START,
});

export const resetChatAlert = () => ({
    payload: {},
    meta: {
        reset: true,
    },
    type: COLLECTIONS.COLLECTION_CHAT.UPDATES.RECEIVED,
});

export const resetChatState = ({ namespace, id, meta }) => ({
    payload: {
        namespace,
        id,
    },
    meta,
    type: COLLECTIONS.COLLECTION_CHAT.RESET,
});

export const heartbeatCheck = () => {
    return {
        type: COLLECTIONS.COLLECTION.HEARTBEAT,
    };
};

export const syncErrorClear = () => {
    return {
        type: COLLECTIONS.COLLECTION.ERROR.SYNC_ERROR_CLEAR,
    };
};

export interface ICollectionSyncErrorThrowAction {
    type: string;
    payload: Error;
}
export const syncErrorThrow = (error: Error): ICollectionSyncErrorThrowAction => {
    return {
        type: COLLECTIONS.COLLECTION.ERROR.SYNC_ERROR_THROW,
        payload: error,
    };
};

export const createCollectionExportItemsPO = (payload) => {
    return {
        payload,
        type: COLLECTIONS.COLLECTION.EXPORT_ITEMS.START,
    };
};

export interface ICollectionSetDoorsFilterAction {
    type: string;
    payload: IDoor[];
}

export interface ILookupAssortmentErrorsAction {
    type: string;
    payload: { assortmentsData?: Array<{ [key: string]: string }> };
}

export interface IAssortmentErrorAction {
    type: string;
    payload: { errorMessage?: string };
}

export const lookupAssortmentErrors = (
    assortmentsData?: Array<{ [key: string]: string }>
): ILookupAssortmentErrorsAction => ({
    type: COLLECTIONS.COLLECTION.ERROR.ALLOCATION_LOOK_UP_ERROR,
    payload: {
        assortmentsData,
    },
});

export const setAssortmentError = (errorMessage?: string): IAssortmentErrorAction => ({
    type: COLLECTIONS.COLLECTION.ERROR.ALLOCATION_LOOK_UP_SET_ERROR,
    payload: {
        errorMessage,
    },
});

export const fetchPinnedRows = (
    collectionId: string,
    showTotals: boolean,
    dimensionPlanMetrics: string[],
    accessPlan: boolean,
    poi?: string
) => ({
    type: COLLECTIONS.COLLECTION.REVIEW_PLANS.ROWS.RELOAD,
    payload: {
        collectionId,
        showTotals,
        dimensionPlanMetrics,
        accessPlan,
        poi,
    },
});

export const fetchServerRollupSnapshotItem = (
    merchantId: string,
    snapshotId: string,
    itemId: string,
    collectionId: string,
    itemDisplayProperty: List<Map<string, any>>
) => ({
    payload: {
        merchantId,
        snapshotId,
        itemId,
        collectionId,
        itemDisplayProperty,
    },
    type: COLLECTIONS.COLLECTION.ROLLUP_SNAPSHOT_ITEM.FETCH.START,
});

export const setServerRollupSnapshotItem = (itemId: string, collectionId: string, product: ISnapshotItem) => ({
    payload: {
        itemId,
        collectionId,
        product,
    },
    type: COLLECTIONS.COLLECTION.ROLLUP_SNAPSHOT_ITEM.SET_ITEM,
});

export const startCreateRollupSnapshot = () => ({
    type: COLLECTIONS.COLLECTION.DRIVE_ITEM.CREATE_ROLLUP_SNAPSHOT.INIT,
});

export const createRollupSnapshotAction = (merchantId, rollupId, collectionIds) => ({
    type: COLLECTIONS.COLLECTION.DRIVE_ITEM.CREATE_ROLLUP_SNAPSHOT.START,
    payload: {
        merchantId,
        rollupItemId: rollupId,
        collectionIds: collectionIds,
    },
});

export const fetchExportLogs = (collectionId: string, refetch?: boolean) => ({
    type: COLLECTIONS.COLLECTION.EXPORT.LOGS.FETCH.START,
    payload: { collectionId, refetch },
});

export const completeFetchingExportLogs = (results, hasMoreExportLogs, shouldAppend) => ({
    type: COLLECTIONS.COLLECTION.EXPORT.LOGS.FETCH.COMPLETED,
    payload: { results, hasMoreExportLogs, shouldAppend },
});

export const clearExportLog = () => ({
    type: COLLECTIONS.COLLECTION.EXPORT.LOGS.CLEAR,
});

export const setImageCellDimensions = ({ width, height }: IImageCellDimensions) => ({
    payload: {
        width,
        height,
    },
    type: COLLECTIONS.COLLECTION.GRID.SET_IMAGE_CELL_DIMENSIONS,
});

export const fetchCollectionDebuggerRevision = (collectionId, debuggerRevision) => {
    return {
        payload: { collectionId, debuggerRevision },
        type: COLLECTIONS.COLLECTION.DEBUGGER_REVISION.FETCH,
    };
};

export const updateCollectionReports = (totals: IDoorTotals) => ({
    payload: { totals },
    type: COLLECTIONS.COLLECTION.REPORTING.UPDATES.START,
});

export const setTransientExportCount = (exportCount: number) => ({
    payload: { exportCount },
    type: COLLECTIONS.COLLECTION.REPORTING.UPDATES.TRANSIENT,
});
